import React, { useState } from 'react';
import { Phone, Info, Loader2 } from 'lucide-react';
import { callService } from '../services/callService';

const CallManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    conferenceNumber: '+91',
    customerName: '',
    passcode: '',
    pincode: '',
    quarter: 'Q1',
    callDuration: '00:30:00',
    isRecorded: false,
  });
  const [formErrors, setFormErrors] = useState({});

  // Remove unused connection stats fetching

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate form
    const validation = callService.validateCallData(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      const result = await callService.initiateCall(formData);

      if (result.status === 'success') {
        setSuccess(`Call initiated successfully! Call ID: ${result.call_id}`);

        // Reset form
        setFormData({
          conferenceNumber: '+91',
          customerName: '',
          passcode: '',
          pincode: '',
          quarter: 'Q1',
          callDuration: '00:30:00',
          isRecorded: false,
        });
        setFormErrors({});
      } else {
        setError(result.message || result.error || 'Failed to initiate call');
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Call Management</h1>
            <p className="dashboard-subtitle">Initiate and manage phone calls through the Knowlarity API</p>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="error-container">
            <div className="error-title">Error</div>
            <div className="error-message">
              {typeof error === 'string' ? error : error.message || 'An unexpected error occurred'}
            </div>
          </div>
        )}

        {success && (
          <div className="success-container">
            <div className="success-message">{success}</div>
          </div>
        )}

        {/* Call Initiation Form */}
        <div className="form-section">
          <h2 className="form-title">Initiate New Call</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="label">Conference Number *</label>
                <input
                  type="text"
                  value={formData.conferenceNumber}
                  onChange={(e) => handleFormChange('conferenceNumber', e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  className={`input ${formErrors.conferenceNumber ? 'error' : ''}`}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Must start with +91 and be 10 digits (e.g., +919876543210)
                </div>
                {formErrors.conferenceNumber && (
                  <div className="error-text">{formErrors.conferenceNumber}</div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Customer Name *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleFormChange('customerName', e.target.value)}
                  placeholder="Enter customer name"
                  className={`input ${formErrors.customerName ? 'error' : ''}`}
                />
                {formErrors.customerName && (
                  <div className="error-text">{formErrors.customerName}</div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Passcode *</label>
                <input
                  type="text"
                  value={formData.passcode}
                  onChange={(e) => handleFormChange('passcode', e.target.value)}
                  placeholder="123456#"
                  className={`input ${formErrors.passcode ? 'error' : ''}`}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Numeric, max 10 digits, must end with # (e.g., 123456#)
                </div>
                {formErrors.passcode && (
                  <div className="error-text">{formErrors.passcode}</div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Pincode *</label>
                <input
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => handleFormChange('pincode', e.target.value)}
                  placeholder="789#"
                  className={`input ${formErrors.pincode ? 'error' : ''}`}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Numeric, max 10 digits, must end with # (e.g., 789#)
                </div>
                {formErrors.pincode && (
                  <div className="error-text">{formErrors.pincode}</div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Quarter *</label>
                <select
                  value={formData.quarter}
                  onChange={(e) => handleFormChange('quarter', e.target.value)}
                  className={`select ${formErrors.quarter ? 'error' : ''}`}
                >
                  <option value="Q1">Q1</option>
                  <option value="Q2">Q2</option>
                  <option value="Q3">Q3</option>
                  <option value="Q4">Q4</option>
                </select>
                {formErrors.quarter && (
                  <div className="error-text">{formErrors.quarter}</div>
                )}
              </div>

              <div className="form-group">
                <label className="label">Call Duration *</label>
                <input
                  type="text"
                  value={formData.callDuration}
                  onChange={(e) => handleFormChange('callDuration', e.target.value)}
                  placeholder="00:30:00"
                  className={`input ${formErrors.callDuration ? 'error' : ''}`}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Format: HH:MM:SS (e.g., 00:30:00) or just minutes (e.g., 30). Max 2 hours.
                </div>
                {formErrors.callDuration && (
                  <div className="error-text">{formErrors.callDuration}</div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isRecorded}
                  onChange={(e) => handleFormChange('isRecorded', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px', color: '#374151' }}>
                  Recorded Call (background retry task will be skipped)
                </span>
              </label>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
                style={{ minWidth: '140px' }}
              >
                {loading ? (
                  <>
                    <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                    <span>Initiating...</span>
                  </>
                ) : (
                  <>
                    <Phone style={{ width: '16px', height: '16px' }} />
                    <span>Initiate Call</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Information Box */}
        <div className="info-box">
          <div className="info-title">
            <Info style={{ width: '16px', height: '16px' }} />
            Call Management Requirements
          </div>
          <ul className="info-list">
            <li><strong>Conference Number:</strong> Must start with +91 and be exactly 10 digits</li>
            <li><strong>Passcode & Pincode:</strong> Both required, numeric, max 10 digits, must end with #</li>
            <li><strong>Call Duration:</strong> HH:MM:SS format, maximum 2 hours (02:00:00)</li>
            <li><strong>Customer Name:</strong> Required, alphanumeric characters only</li>
            <li><strong>Recorded Calls:</strong> Skip background retry mechanism when enabled</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CallManagement; 